import { useNavigate } from 'react-router-dom';
import { MapPin, Phone, Mail, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';

// Custom icons for social platforms
const FacebookIcon = () => (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
);

const YoutubeIcon = () => (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
);

const TelegramIcon = () => (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M11.944 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0a12 12 0 0 0-.056 0zm4.962 7.224c.1-.002.321.023.465.14a.506.506 0 0 1 .171.325c.016.093.036.306.02.472-.18 1.898-.962 6.502-1.36 8.627-.168.9-.499 1.201-.82 1.23-.696.065-1.225-.46-1.9-.902-1.056-.693-1.653-1.124-2.678-1.8-1.185-.78-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.14-5.061 3.345-.48.33-.913.49-1.302.48-.428-.008-1.252-.241-1.865-.44-.752-.245-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635z"/></svg>
);

const DailyHuntIcon = () => (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.95-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/></svg>
);

const JoshIcon = () => (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.41 0-8-3.59-8-8s3.59-8 8-8 8 3.59 8 8-3.59 8-8 8zm-5.5-2.5l7.51-3.49L17.5 6.5 9.99 9.99 6.5 17.5zm5.5-6.6c.61 0 1.1.49 1.1 1.1s-.49 1.1-1.1 1.1-1.1-.49-1.1-1.1.49-1.1 1.1-1.1z"/></svg>
);

const ShareChatIcon = () => (
  <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24"><path d="M18 16.08c-.76 0-1.44.3-1.96.77L8.91 12.7c.05-.23.09-.46.09-.7s-.04-.47-.09-.7l7.05-4.11c.54.5 1.25.81 2.04.81 1.66 0 3-1.34 3-3s-1.34-3-3-3-3 1.34-3 3c0 .24.04.47.09.7L8.04 9.81C7.5 9.31 6.79 9 6 9c-1.66 0-3 1.34-3 3s1.34 3 3 3c.79 0 1.5-.31 2.04-.81l7.12 4.16c-.05.21-.08.43-.08.65 0 1.61 1.31 2.92 2.92 2.92s2.92-1.31 2.92-2.92-1.31-2.92-2.92-2.92z"/></svg>
);

const Footer = () => {
  const navigate = useNavigate();
  const currentYear = new Date().getFullYear();

  const socialLinks = [
    { icon: FacebookIcon, url: 'https://www.facebook.com/profile.php?id=100093562119110', label: 'Facebook' },
    { icon: YoutubeIcon, url: 'https://www.youtube.com/channel/UCLUTgkajQTy9vjyt_jNm0Bg', label: 'YouTube' },
    { icon: TelegramIcon, url: 'https://t.me/VVVSICAREERGUIDELINES', label: 'Telegram' },
    { icon: DailyHuntIcon, url: 'https://profile.dailyhunt.in/employmentnews', label: 'Daily Hunt' },
    { icon: JoshIcon, url: 'https://share.myjosh.in/profile/b752e9ed-8686-49ce-8fcd-23e39cc1353f?u=0x75575b4426724c91', label: 'Josh' },
    { icon: ShareChatIcon, url: 'https://sharechat.com/profile/veerarahavan1888?d=n', label: 'ShareChat' },
  ];

  return (
    <footer className="bg-slate-900 text-white mt-16">
      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          
          {/* Company Info */}
          <div className="lg:col-span-1">
            <div className="flex items-center mb-4">
              <img src="/vvvsi-logo.png" alt="VVVSI Logo" className="h-16 w-auto object-contain bg-white rounded-lg p-1" />
            </div>
            <p className="text-slate-400 text-sm leading-relaxed mb-4">
              Vallalar Velai Vaaipu Sevai Iyakkam - A Trust for Employment, Career Guidance, Education, Health & Social Welfare.
            </p>
            <div className="flex flex-wrap gap-2 mb-4">
              {socialLinks.map((social, index) => (
                <a 
                  key={index}
                  href={social.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  title={social.label}
                  className="w-9 h-9 bg-slate-800 hover:bg-teal-600 rounded-full flex items-center justify-center transition-colors"
                >
                  <social.icon />
                </a>
              ))}
            </div>
            {/* Donate Button */}
            <Button 
              onClick={() => navigate('/donate')}
              className="w-full bg-gradient-to-r from-rose-500 to-pink-600 hover:from-rose-600 hover:to-pink-700 text-white font-semibold shadow-lg"
            >
              <Heart className="w-4 h-4 mr-2" />
              Donate Us
            </Button>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-white">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <a onClick={() => navigate('/')} className="text-slate-400 hover:text-teal-400 cursor-pointer transition-colors text-sm">
                  Browse Jobs
                </a>
              </li>
              <li>
                <a onClick={() => navigate('/about')} className="text-slate-400 hover:text-teal-400 cursor-pointer transition-colors text-sm">
                  About Us
                </a>
              </li>
              <li>
                <a onClick={() => navigate('/achievements')} className="text-slate-400 hover:text-teal-400 cursor-pointer transition-colors text-sm">
                  Achievements
                </a>
              </li>
              <li>
                <a onClick={() => navigate('/our-clients')} className="text-slate-400 hover:text-teal-400 cursor-pointer transition-colors text-sm">
                  Our Clients
                </a>
              </li>
              <li>
                <a onClick={() => navigate('/contact')} className="text-slate-400 hover:text-teal-400 cursor-pointer transition-colors text-sm">
                  Contact Us
                </a>
              </li>
              <li>
                <a onClick={() => navigate('/faq')} className="text-slate-400 hover:text-teal-400 cursor-pointer transition-colors text-sm">
                  FAQ
                </a>
              </li>
            </ul>
          </div>

          {/* Our Services */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-white">Our Services</h3>
            <ul className="space-y-2">
              <li>
                <a onClick={() => navigate('/job-notification')} className="text-slate-400 hover:text-teal-400 cursor-pointer transition-colors text-sm">
                  Job Notification
                </a>
              </li>
              <li>
                <a onClick={() => navigate('/career-guidance')} className="text-slate-400 hover:text-teal-400 cursor-pointer transition-colors text-sm">
                  Career Guidance
                </a>
              </li>
              <li>
                <a onClick={() => navigate('/job-fairs')} className="text-slate-400 hover:text-teal-400 cursor-pointer transition-colors text-sm">
                  Job Fairs
                </a>
              </li>
              <li>
                <a onClick={() => navigate('/entrepreneurship')} className="text-slate-400 hover:text-teal-400 cursor-pointer transition-colors text-sm">
                  Entrepreneurship
                </a>
              </li>
              <li>
                <a onClick={() => navigate('/education-support')} className="text-slate-400 hover:text-teal-400 cursor-pointer transition-colors text-sm">
                  Education Support
                </a>
              </li>
              <li>
                <a onClick={() => navigate('/medical-support')} className="text-slate-400 hover:text-teal-400 cursor-pointer transition-colors text-sm">
                  Medical Support
                </a>
              </li>
            </ul>
          </div>

          {/* Contact Info */}
          <div>
            <h3 className="text-lg font-semibold mb-4 text-white">Contact Us</h3>
            <ul className="space-y-3">
              <li className="flex items-start gap-3 text-sm text-slate-400">
                <MapPin className="w-4 h-4 mt-0.5 text-teal-500 flex-shrink-0" />
                <span>Vallalar Velai Vaaipu Sevai Iyakkam, No-122, 2nd Floor, Venugopal Nagar, Opp to Saibaba temple, Kirumambakkam, Puducherry UT, India – 607402</span>
              </li>
              <li className="flex items-center gap-3 text-sm text-slate-400">
                <Phone className="w-4 h-4 text-teal-500 flex-shrink-0" />
                <span>+91 82705 27992</span>
              </li>
              <li className="flex items-center gap-3 text-sm text-slate-400">
                <Mail className="w-4 h-4 text-teal-500 flex-shrink-0" />
                <span>info@vvvsi.com</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-slate-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 text-sm text-center md:text-left">
              © {currentYear} Vallalar Velai Vaaipu Sevai Iyakkam. All rights reserved.
            </p>
            <div className="flex gap-6 text-sm">
              <a onClick={() => navigate('/privacy-policy')} className="text-slate-500 hover:text-teal-400 cursor-pointer transition-colors">Privacy Policy</a>
              <a onClick={() => navigate('/faq')} className="text-slate-500 hover:text-teal-400 cursor-pointer transition-colors">FAQ</a>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
